<template>
  <div>
    <span v-if="t.label && t.label.length > 1" :class="{ invalid: invalid }">{{
      t.label
    }}</span>
    <VForm ref="expForm" class="expiration-container">
      <NsSelect
        v-model="expMonth"
        :items="months"
        name="expMonth"
        :disabled="fieldDisabled"
        :placeholder="t.monthPlaceholder"
        autocomplete="cc-exp-month"
        class="exp-month"
        :rules="monthRules"
        @input="dateUpdated"
        @focus="setFocus"
        @blur="blurringExpMonth"
      ></NsSelect>
      <NsSelect
        v-model="expYear"
        :items="years"
        :disabled="fieldDisabled"
        name="expYear"
        class="exp-year"
        autocomplete="cc-exp-year"
        :placeholder="t.yearPlaceholder"
        :rules="rules"
        validate-on-blur
        @input="dateUpdated"
        @focus="setFocus"
        @blur="blurringExpYear"
      ></NsSelect>
    </VForm>
  </div>
</template>

<script>
/**
 * Original Author: Paul Roberts
 * Original Team: Mavericks
 */
import NsSelect from '../NsSelect.vue';
import { VForm } from 'vuetify/lib';
import MONTHS from './months';

export default {
  name: 'NsCreditCardDate',
  components: {
    NsSelect,
    VForm,
  },
  props: {
    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          label: 'Expiration Date',
          monthPlaceholder: '',
          yearPlaceholder: '',
          fieldRequired: 'Field is required',
          invalidDate: 'Invalid Date Entered',
        };
      },
    },
    value: {
      type: Object,
      default() {
        return {
          expMonth: '',
          expYear: '',
        };
      },
    },
    /**
     * Field Disabled (for edit mode)
     */
    fieldDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expMonth: this.value.expMonth,
      expYear: this.value.expYear,
      expDateValid: true,
      months: MONTHS,
      invalid: false,
      years: [],
      rules: [],
      monthRules: [],
    };
  },
  computed: {
    t() {
      return this.translations;
    },
  },
  watch: {
    // If the value passed into the component changes then update the dateString v-model value
    value() {
      if (
        !this.expMonth ||
        !this.expYear ||
        parseInt(this.value.expMonth) !== parseInt(this.expMonth) ||
        parseInt(this.value.expYear) !== parseInt(this.expYear)
      ) {
        this.expMonth = `${this.value.expMonth}`;
        this.expYear = `${this.value.expYear}`;
      }
    },
    t() {
      if (Object.keys(this.t).length > 0) {
        // This is a hack.  The only way I have found to reset the rules with new translations
        // is to clear them out and wait for a while then reset them
        this.rules = this.monthRules = [];
        setTimeout(() => this.initDateRules(), 10);
      }
    },
  },
  mounted() {
    this.setYears();
    this.initDateRules();
  },
  methods: {
    setYears() {
      const currYear = new Date().getFullYear();
      const yrs = [];
      for (let i = 0; i < 11; i++) {
        const yr = (currYear + i).toString();
        yrs.push({
          text: yr,
          value: yr.slice(2, 4),
        });
      }
      this.years = yrs;
    },
    setFocus() {
      this.$emit('date-valid-state', true);
    },
    blurringExpMonth() {
      this.monthBlurred = true;
      this.invalid = !(
        this.expMonth &&
        (!this.yearBlurred || this.isDateValid())
      );
      this.$emit('date-valid-state', this.isDateValid());
    },
    blurringExpYear() {
      this.yearBlurred = true;
      this.$refs.expForm.validate();
      this.invalid = !(this.expYear && this.isDateValid());
      this.$emit('date-valid-state', this.isDateValid());
    },
    dateUpdated() {
      // If the date is invalid and it was just updated then check again
      if (this.invalid) {
        this.invalid = !this.isDateValid();
      }
      if (this.expMonth && this.expYear) {
        // When emitting objects it needs to be a new object
        this.$emit('input', {
          ...this.value,
          expMonth: this.expMonth,
          expYear: this.expYear,
        });
      } else if (this.value.expMonth && this.value.expYear) {
        // If we have a current value clear it if the value is now invalid
        this.$emit('input', { ...this.value, expMonth: null, expYear: null });
      }
    },
    isDateValid() {
      let valid = true;
      if (this.expMonth && this.expYear) {
        const today = new Date();
        const year = today.getYear() - 100;
        const month = today.getMonth() + 1;
        valid = parseInt(this.expYear) > year || this.expMonth >= month;
      } else {
        valid = false;
      }
      return valid;
    },
    isValidMonth() {
      const today = new Date();
      const year = today.getYear() - 100;
      const month = today.getMonth() + 1;
      return (
        this.expMonth &&
        (!this.expYear ||
          parseInt(this.expYear) > year ||
          this.expMonth >= month)
      );
    },
    validateMonth() {
      return this.isValidMonth() || this.t.invalidDate;
    },
    validate() {
      this.invalid = !this.isDateValid();
      return this.$refs.expForm.validate();
    },
    reset() {
      this.$refs.expForm.reset();
    },
    initDateRules() {
      this.monthRules = [
        (v) => !!v || this.t.fieldRequired,
        this.validateMonth,
      ];
      this.rules = [(v) => !!v || this.t.fieldRequired];
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";

::v-deep * {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}
.invalid {
  color: map.get(colors.$baseColors, 'color-error');
}
.expiration-container {
  display: flex;
  width: 100%;
  .exp-month {
    width: 45%;
    padding-right: 8px;
  }
  .exp-year {
    width: 55%;
  }
}
</style>
